// File: app/src/apis/llm/diagnostics.js
//
// Per-code counts for one generation: which deterministic REPAIRS fired, and
// which validation issues survived them.
//
// The point is deletion, not dashboards. The pipeline carries roughly a dozen
// repair passes, each added to defend against a real failure by some model at
// some point, and until now there was no way to tell a pass that quietly saves
// every generation from one that has not fired since the model that needed it
// was retired. A repair with a count of zero over a few hundred generations is
// a pass you can delete with evidence; a validation code with a count of zero
// is a check you can delete with evidence. Without the counts, both are
// arguments.
//
// Deliberately pure, for the same reason as usage.js: anything importing
// providers.js pulls in the ESM-only Gemini SDK, which Jest cannot transform.

// A mutable tally with the collector the normaliser expects. One per
// generation ATTEMPT — a truncation retry and the refinement pass each get
// their own, so the counts distinguish "fired once" from "fired every time".
export const createRepairLog = () => {
  const counts = {};
  return {
    counts,
    onRepair: (code) => {
      if (!code) return;
      counts[code] = (counts[code] || 0) + 1;
    },
  };
};

// Validation issues → the same {code: count} shape, so both halves aggregate
// identically downstream. Severity is kept separately rather than folded into
// the code: a code's severity is a property of the validator, not of the run,
// and duplicating it per row would just invite the two to disagree.
export const countValidationIssues = (issues) => {
  const counts = {};
  (Array.isArray(issues) ? issues : []).forEach((issue) => {
    const code = issue?.code;
    if (!code) return;
    counts[code] = (counts[code] || 0) + 1;
  });
  return counts;
};

const mergeCounts = (into, from) => {
  Object.entries(from || {}).forEach(([code, n]) => {
    const count = Number(n);
    if (!Number.isFinite(count) || count <= 0) return;
    into[code] = (into[code] || 0) + count;
  });
  return into;
};

/**
 * Fold every attempt of one generation into a single record.
 *
 * @param {Array<{provider?:string, model?:string, refinement?:boolean,
 *                repairs?:Object, validation?:Object, blocking?:boolean}>} entries
 * @returns {Object|null} null when there is nothing to report.
 */
export const accumulateDiagnostics = (entries) => {
  const rows = (Array.isArray(entries) ? entries : []).filter(Boolean);
  if (rows.length === 0) return null;

  const last = rows[rows.length - 1];
  const refinementRows = rows.filter((row) => row.refinement);

  return {
    provider: last.provider ?? null,
    model: last.model ?? null,
    attempts: rows.length,
    // Did the plan need a second paid generation, and did that clear the
    // blockers? `resolved` is null when no refinement ran, so "never needed
    // one" and "needed one and failed" don't collapse into the same figure.
    refined: refinementRows.length > 0,
    resolved:
      refinementRows.length > 0
        ? refinementRows.some((row) => row.blocking === false)
        : null,
    repairs: rows.reduce((acc, row) => mergeCounts(acc, row.repairs), {}),
    validation: rows.reduce((acc, row) => mergeCounts(acc, row.validation), {}),
  };
};

// Flatten an aggregate — the API's per-code totals — into rows ordered by how
// often each fired, rarest last. The rare end is the interesting end: that is
// where the deletion candidates are.
export const rankDiagnosticCodes = (counts) =>
  Object.entries(counts || {})
    .map(([code, count]) => ({ code, count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
